/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppLoader from "components/AppLoader";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import rolesTableData from "layouts/tables/data/rolesTableData";
import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";

import { useEffect, useState } from "react";

function Roles() {
  const [roles, setRoles] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [statusUpdatingRoleId, setStatusUpdatingRoleId] = useState(null);
  const [error, setError] = useState(null);

  const [openAssignEndpoints, setOpenAssignEndpoints] = useState(false);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedStatusRole, setSelectedStatusRole] = useState(null);
  const [assignedEndpoints, setAssignedEndpoints] = useState([]);
  const [unassignedEndpoints, setUnassignedEndpoints] = useState([]);
  const [isAssignmentActive, setIsAssignmentActive] = useState(true);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const getRoleId = (role) => role?.roleId ?? role?.RoleId ?? role?.id ?? role?.Id ?? null;
  const getRoleName = (role) => role?.roleName ?? role?.name ?? role?.Name ?? "";

  const getEndpointId = (endpoint) =>
    endpoint?.endpointId ??
    endpoint?.endPointId ??
    endpoint?.EndPointId ??
    endpoint?.id ??
    endpoint?.Id ??
    endpoint?.endPoint?.id ??
    endpoint?.endPoint?.Id ??
    endpoint?.EndPoint?.id ??
    endpoint?.EndPoint?.Id ??
    null;

  const getEndpointDisplayName = (endpoint) =>
    endpoint?.endpointName ??
    endpoint?.displayName ??
    endpoint?.DisplayName ??
    endpoint?.name ??
    endpoint?.Name ??
    endpoint?.endPoint?.displayName ??
    endpoint?.endPoint?.DisplayName ??
    endpoint?.endPoint?.name ??
    endpoint?.endPoint?.Name ??
    endpoint?.EndPoint?.displayName ??
    endpoint?.EndPoint?.DisplayName ??
    endpoint?.EndPoint?.name ??
    endpoint?.EndPoint?.Name ??
    "";

  const getEndpointRoleId = (endpointRole) =>
    endpointRole?.endpointRoleId ??
    endpointRole?.EndpointRoleId ??
    endpointRole?.id ??
    endpointRole?.Id ??
    null;

  const isActiveRecord = (item) => {
    const value = item?.isActive ?? item?.IsActive;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "active";
    }
    return true;
  };

  const buildGetAllRolesUrl = () => {
    const base = `${appSettings.baseUrl}${appSettings.endpoints.getAllRoles}`;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}_ts=${Date.now()}`;
  };

  const buildGetAllEndpointsUrl = () => {
    const base = `${appSettings.baseUrl}${appSettings.endpoints.getAllEndpoints}`;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}_ts=${Date.now()}`;
  };

  const buildGetEndpointsByRoleUrl = (roleId) => {
    const base = `${appSettings.baseUrl}${appSettings.endpoints.getEndpointsByRoleId}`;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}roleId=${encodeURIComponent(roleId)}&_ts=${Date.now()}`;
  };

  const resolveLatestRoleStatus = (role) => {
    const roleId = getRoleId(role);
    if (!roleId) return false;

    const latestRole = roles.find((item) => String(getRoleId(item)) === String(roleId));
    if (latestRole) {
      return isActiveRecord(latestRole);
    }

    return isActiveRecord(role);
  };

  const resolveBooleanStatus = (value, fallback) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "1" || normalized === "true") return true;
      if (normalized === "0" || normalized === "false") return false;
    }
    return fallback;
  };

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(buildGetAllRolesUrl(), {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("An error occurred while fetching roles.");
      }

      const data = await response.json();
      setRoles(normalizeArray(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEndpoints = async () => {
    try {
      const response = await fetchWithAuth(buildGetAllEndpointsUrl(), {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("An error occurred while fetching endpoints.");
      }

      const data = await response.json();
      const normalized = normalizeArray(data);
      setEndpoints(normalized);
      return normalized;
    } catch (err) {
      setError(err.message);
      return [];
    }
  };

  const fetchEndpointsByRoleId = async (roleId) => {
    const response = await fetchWithAuth(buildGetEndpointsByRoleUrl(roleId), {
      method: "GET",
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("An error occurred while fetching role endpoint assignments.");
    }

    const data = await response.json();
    return normalizeArray(data);
  };

  const buildEndpointLists = (allEndpoints, endpointRoles) => {
    const endpointNameById = new Map(
      allEndpoints
        .map((item) => [getEndpointId(item), getEndpointDisplayName(item)])
        .filter(([id]) => id !== null)
        .map(([id, name]) => [String(id), name])
    );

    const assigned = endpointRoles
      .map((item) => {
        const endpointId = getEndpointId(item);
        const endpointName =
          endpointNameById.get(String(endpointId)) || getEndpointDisplayName(item);
        return {
          endpointRoleId: getEndpointRoleId(item),
          endpointId,
          endpointName: endpointName || `Endpoint ${endpointId ?? ""}`.trim(),
        };
      })
      .filter((item) => item.endpointId !== null);

    const assignedEndpointIds = new Set(assigned.map((item) => String(item.endpointId)));

    const unassigned = allEndpoints
      .filter((item) => isActiveRecord(item))
      .filter((item) => {
        const endpointId = getEndpointId(item);
        return endpointId !== null && !assignedEndpointIds.has(String(endpointId));
      })
      .map((item) => ({
        endpointId: getEndpointId(item),
        endpointName:
          getEndpointDisplayName(item) || `Endpoint ${getEndpointId(item) ?? ""}`.trim(),
      }));

    return { assigned, unassigned };
  };

  useEffect(() => {
    fetchRoles();
    fetchEndpoints();
  }, []);

  const handleOpenAssignEndpoints = async (role) => {
    const roleId = getRoleId(role);

    if (!roleId) {
      setError("Unable to assign endpoints: role id is missing.");
      return;
    }

    setSelectedRole(role);
    setOpenAssignEndpoints(true);
    setLoadingAssignments(true);
    setError(null);

    try {
      const endpointSource = endpoints.length > 0 ? endpoints : await fetchEndpoints();
      const endpointRoles = await fetchEndpointsByRoleId(roleId);
      const { assigned, unassigned } = buildEndpointLists(endpointSource, endpointRoles);
      setAssignedEndpoints(assigned);
      setUnassignedEndpoints(unassigned);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleCloseAssignEndpoints = () => {
    if (assigning) return;

    setOpenAssignEndpoints(false);
    setSelectedRole(null);
    setAssignedEndpoints([]);
    setUnassignedEndpoints([]);
    setIsAssignmentActive(true);
  };

  const refreshSelectedRoleAssignments = async () => {
    const roleId = getRoleId(selectedRole);
    if (!roleId) return;

    const endpointRoles = await fetchEndpointsByRoleId(roleId);
    const sourceEndpoints = endpoints.length > 0 ? endpoints : await fetchEndpoints();
    const { assigned, unassigned } = buildEndpointLists(sourceEndpoints, endpointRoles);
    setAssignedEndpoints(assigned);
    setUnassignedEndpoints(unassigned);
  };

  const handleAssignEndpoint = async (endpoint) => {
    const roleId = getRoleId(selectedRole);

    if (!roleId) {
      setError("Unable to assign endpoint: role id is missing.");
      return;
    }

    if (!endpoint?.endpointId) {
      setError("Please select an endpoint before assigning.");
      return;
    }

    setAssigning(true);
    setError(null);

    const url = `${appSettings.baseUrl}${
      appSettings.endpoints.createEndPointRole
    }?endpointId=${encodeURIComponent(endpoint.endpointId)}&roleId=${encodeURIComponent(
      roleId
    )}&isActive=${isAssignmentActive ? 1 : 0}`;

    try {
      const response = await fetchWithAuth(url, { method: "POST" });

      if (!response.ok) {
        let errorMessage = "An error occurred while assigning endpoint.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error message when response body is not JSON.
        }
        throw new Error(errorMessage);
      }

      await refreshSelectedRoleAssignments();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveEndpoint = async (endpoint) => {
    if (!endpoint?.endpointRoleId) {
      setError("Unable to remove endpoint: assignment id is missing.");
      return;
    }

    setAssigning(true);
    setError(null);

    const formBody = new URLSearchParams();
    formBody.append("id", String(endpoint.endpointRoleId));
    const url = `${appSettings.baseUrl}${appSettings.endpoints.deleteEndpointRole}`;

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while removing endpoint.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error message when response body is not JSON.
        }
        throw new Error(errorMessage);
      }

      await refreshSelectedRoleAssignments();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleDragStart = (event, endpoint, source) => {
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        source,
        endpointId: endpoint.endpointId,
        endpointRoleId: endpoint.endpointRoleId,
      })
    );
  };

  const handleDropOnAssigned = async (event) => {
    event.preventDefault();
    if (assigning) return;

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    if (!payload || payload.source !== "unassigned") return;

    const endpoint = unassignedEndpoints.find(
      (item) => String(item.endpointId) === String(payload.endpointId)
    );

    if (!endpoint) return;

    await handleAssignEndpoint(endpoint);
  };

  const handleDropOnUnassigned = async (event) => {
    event.preventDefault();
    if (assigning) return;

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    if (!payload || payload.source !== "assigned") return;

    const endpoint = assignedEndpoints.find(
      (item) =>
        String(item.endpointRoleId ?? item.endpointId) ===
        String(payload.endpointRoleId ?? payload.endpointId)
    );

    if (!endpoint) return;

    await handleRemoveEndpoint(endpoint);
  };

  const handleToggleRoleStatus = async (role) => {
    const roleId = getRoleId(role);

    if (!roleId) {
      setError("Unable to update role status: role id is missing.");
      return;
    }

    const currentIsActive = resolveLatestRoleStatus(role);
    const nextIsActive = currentIsActive ? 0 : 1;

    setStatusUpdatingRoleId(roleId);
    setError(null);

    const url = `${appSettings.baseUrl}${appSettings.endpoints.toggleRoleStatus}`;
    const formBody = new URLSearchParams();
    formBody.append("roleId", String(roleId));
    formBody.append("isActive", String(nextIsActive));

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while updating role status.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error text when API doesn't return JSON.
        }
        throw new Error(errorMessage);
      }

      let responseData = null;
      try {
        responseData = await response.json();
      } catch (e) {
        // Keep fallback behavior when API response body is empty.
      }

      const persistedIsActive = resolveBooleanStatus(responseData?.isActive, !!nextIsActive);

      setRoles((prev) =>
        prev.map((item) => {
          const itemRoleId = getRoleId(item);
          if (String(itemRoleId) !== String(roleId)) return item;
          return { ...item, isActive: persistedIsActive, IsActive: persistedIsActive };
        })
      );

      setSelectedRole((prev) => {
        if (!prev) return prev;
        const selectedRoleId = getRoleId(prev);
        if (String(selectedRoleId) !== String(roleId)) return prev;
        return { ...prev, isActive: persistedIsActive, IsActive: persistedIsActive };
      });

      setSelectedStatusRole((prev) => {
        if (!prev) return prev;
        const selectedRoleId = getRoleId(prev);
        if (String(selectedRoleId) !== String(roleId)) return prev;
        return { ...prev, isActive: persistedIsActive, IsActive: persistedIsActive };
      });

      await fetchRoles();
    } catch (err) {
      setError(err.message);
    } finally {
      setStatusUpdatingRoleId(null);
    }
  };

  const handleOpenStatusConfirm = (role) => {
    const roleId = getRoleId(role);
    if (!roleId) {
      setError("Unable to update role status: role id is missing.");
      return;
    }

    const latestRole = roles.find((item) => String(getRoleId(item)) === String(roleId));
    setSelectedStatusRole(latestRole ?? role);
    setOpenStatusConfirm(true);
  };

  const handleCloseStatusConfirm = () => {
    if (statusUpdatingRoleId !== null) return;
    setOpenStatusConfirm(false);
    setSelectedStatusRole(null);
  };

  const handleConfirmToggleStatus = async () => {
    if (!selectedStatusRole) return;
    await handleToggleRoleStatus(selectedStatusRole);
    setOpenStatusConfirm(false);
    setSelectedStatusRole(null);
  };

  const { columns, rows } = rolesTableData(
    roles,
    handleOpenAssignEndpoints,
    handleOpenStatusConfirm,
    statusUpdatingRoleId
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDTypography variant="h6" color="white">
                  Roles Table
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader />
                  </MDBox>
                ) : error ? (
                  <MDBox p={3}>
                    <MDTypography variant="button" color="error">
                      Error: {error}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    canSearch
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog
        open={openAssignEndpoints}
        onClose={handleCloseAssignEndpoints}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="medium">
            Endpoint Assignment
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" pt={1} pb={1}>
            <MDBox
              mb={2}
              px={2}
              py={1.5}
              borderRadius="lg"
              bgColor="light"
              border="1px solid"
              borderColor="light"
            >
              <MDTypography variant="button" color="dark" fontWeight="bold">
                Role: {getRoleName(selectedRole) || "-"}
              </MDTypography>
            </MDBox>
            <Divider />

            {loadingAssignments ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                <AppLoader size={30} />
              </MDBox>
            ) : (
              <MDBox mb={2}>
                <MDBox
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                  gap={2}
                  mt={2}
                >
                  <MDBox
                    p={1.5}
                    border="1px solid"
                    borderColor="secondary"
                    borderRadius="lg"
                    minHeight="220px"
                    bgColor="light"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropOnUnassigned}
                  >
                    <MDTypography variant="button" color="secondary" fontWeight="bold" mb={1.5}>
                      Unassigned Endpoints
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {unassignedEndpoints.map((endpoint) => (
                        <MDBox
                          key={`unassigned-${endpoint.endpointId}`}
                          p={1}
                          border="1px solid"
                          borderColor="secondary"
                          borderRadius="md"
                          bgColor="light"
                          draggable={!assigning}
                          onDragStart={(e) => handleDragStart(e, endpoint, "unassigned")}
                          sx={{
                            cursor: assigning ? "not-allowed" : "grab",
                            transition: "all 0.15s ease-in-out",
                            "&:hover": {
                              backgroundColor: "#eceff1",
                            },
                          }}
                        >
                          <MDTypography variant="caption" fontWeight="medium">
                            {endpoint.endpointName}
                          </MDTypography>
                        </MDBox>
                      ))}
                      {unassignedEndpoints.length === 0 && (
                        <MDTypography variant="caption" color="text" opacity={0.8}>
                          No endpoints available
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>

                  <MDBox
                    p={1.5}
                    border="1px solid"
                    borderColor="success"
                    borderRadius="lg"
                    minHeight="220px"
                    sx={{ backgroundColor: "rgba(76, 175, 80, 0.08)" }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropOnAssigned}
                  >
                    <MDTypography variant="button" color="success" fontWeight="bold" mb={1.5}>
                      Assigned Endpoints
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {assignedEndpoints.map((endpoint) => (
                        <MDBox
                          key={`assigned-${endpoint.endpointRoleId ?? endpoint.endpointId}`}
                          p={1}
                          border="1px solid"
                          borderColor="success"
                          borderRadius="md"
                          draggable={!assigning}
                          onDragStart={(e) => handleDragStart(e, endpoint, "assigned")}
                          sx={{
                            backgroundColor: "rgba(76, 175, 80, 0.16)",
                            cursor: assigning ? "not-allowed" : "grab",
                            transition: "all 0.15s ease-in-out",
                            "&:hover": {
                              backgroundColor: "rgba(76, 175, 80, 0.24)",
                            },
                          }}
                        >
                          <MDTypography variant="caption" fontWeight="medium">
                            {endpoint.endpointName}
                          </MDTypography>
                        </MDBox>
                      ))}
                      {assignedEndpoints.length === 0 && (
                        <MDTypography variant="caption" color="text" opacity={0.8}>
                          No assigned endpoints
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            )}

            <FormControlLabel
              sx={{ mt: 0.5, ml: 0.5 }}
              control={
                <Switch
                  checked={isAssignmentActive}
                  onChange={(e) => setIsAssignmentActive(e.target.checked)}
                />
              }
              label="Assign as active"
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseAssignEndpoints}
            color="secondary"
            variant="outlined"
            disabled={assigning}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openStatusConfirm} onClose={handleCloseStatusConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to{" "}
            {selectedStatusRole?.isActive ?? selectedStatusRole?.IsActive ?? false
              ? "deactivate"
              : "activate"}{" "}
            this role?
          </MDTypography>
          {selectedStatusRole && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {getRoleName(selectedStatusRole) || "-"}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseStatusConfirm}
            color="secondary"
            disabled={statusUpdatingRoleId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmToggleStatus}
            variant="gradient"
            color={
              selectedStatusRole?.isActive ?? selectedStatusRole?.IsActive ?? false
                ? "error"
                : "success"
            }
            disabled={statusUpdatingRoleId !== null}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Roles;
